import { debounce, Dictionary } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FetchableResourceType,
  ListOfResourcesToBeFetched,
  LoadAllocationsData,
  ResourceAllocations,
  ResourcesToBeFetched,
} from 'api/resource-management/types';
import {
  useEventListenerForEventStore,
  useEventListenerForResourceStore,
  useEventListenerForScheduler,
} from 'components/organisms/resource-management/scheduler-config/listeners';
import { useLoadingRenderer } from 'components/organisms/resource-management/scheduler-config/loading-renderer';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import { getInitialResourcesToBeFetched } from 'components/organisms/resource-management/utils/common';
import { getCorrectedApiDateRangeBasedOnView } from 'components/organisms/resource-management/utils/dates';
import { getNewRangeExcludingExistingRanges } from 'components/organisms/resource-management/utils/pages';
import {
  getInitialVisibleResourcesRange,
  useVisibleResources,
} from 'components/organisms/resource-management/utils/use-visible-resources';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useUser } from 'hooks/use-user';
import { logger } from 'services';
import { queueMacrotask } from 'utils/macrotask';
import { GroupBy, Range, SchedulerReference } from '../types';

interface Props {
  groupBy: GroupBy;
  schedulerRef: SchedulerReference;
  onAllocationLoadHandler: (
    allocations: ResourceAllocations,
    resourcesWithNewData: number[],
    isInitialLoad: boolean,
  ) => Promise<void>;
  loadAllocations: <T extends ListOfResourcesToBeFetched>(
    props: LoadAllocationsData<T>,
  ) => Promise<ResourceAllocations>;
  currentViewPreset: ViewPresetId;
  workingDaysConfig: WorkingDaysConfiguration;
  showLoaderOnDataFetch?: boolean;
  onVisibleResourceChange?: (
    visibleResources: ResourcesToBeFetched<ListOfResourcesToBeFetched>,
  ) => void;
}

interface ResultValue {
  isSchedulerLoadedWithData: boolean;
  rangesLoadedInUI: Range[];
  resourcesLoadedInUI: Dictionary<boolean>;
}

const stringListToNumberList = (input: string[]): number[] =>
  input.map((val) => parseInt(val, 10));

export const useAllocationLoader = ({
  groupBy,
  schedulerRef,
  onAllocationLoadHandler,
  loadAllocations,
  currentViewPreset,
  workingDaysConfig,
  showLoaderOnDataFetch = false,
  onVisibleResourceChange,
}: Props): ResultValue => {
  const [isSchedulerLoadedWithData, setSchedulerLoadedWithData] =
    useState(false);
  const [allLoadedRanges, setAllLoadedRanges] = useState<Range[]>([]);
  const [rangesLoadedInUI, setRangesLoadedInUI] = useState<Range[]>([]);
  const [resourcesLoadedInUI, setResourcesLoadedInUI] = useState<
    Dictionary<boolean>
  >({});
  const [rolesLoadedInUI, setRolesLoadedInUI] = useState<Dictionary<boolean>>(
    {},
  );
  const isCollaborator = useIsCurrentUserCollaborator();
  const {
    user: { userId: currentUserId },
  } = useUser();

  const { showLoading, hideLoading } = useLoadingRenderer({ schedulerRef });

  const {
    getVisibleResources,
    getRoleIdsToBeFetched,
    setCurrentVisibleResources,
    getVisibleProjectIds,
  } = useVisibleResources(schedulerRef, groupBy);

  const loadAllocationsForRange = useCallback(
    (e) => {
      const visibleResources = getVisibleResources();

      const newResources = (
        groupBy === GroupBy.Users
          ? Object.keys(visibleResources.userIdList)
          : Object.keys(visibleResources.projectIdList)
      ).reduce(
        (result, id) => ({
          ...result,
          ...(Object.keys(resourcesLoadedInUI).includes(id.toString())
            ? {}
            : { [id]: true }),
        }),
        {} as Dictionary<boolean>,
      );

      const newRoles = Object.keys(getRoleIdsToBeFetched()).reduce(
        (result, id) => ({
          ...result,
          ...(Object.keys(rolesLoadedInUI).includes(id.toString())
            ? {}
            : { [id]: true }),
        }),
        {} as Dictionary<boolean>,
      );

      /**
       * ! When RM is loaded with filters and the filter is cleared,
       * ! sometimes, loaddaterange is triggered instead of change (filter) event,
       * ! which does not load the events as expected; to figure out the cause and fix if it happens again
       */

      const { startDate: correctedFromDate, endDate: correctedToDate } =
        getCorrectedApiDateRangeBasedOnView(
          {
            fromDate: moment(schedulerRef.current.instance.startDate),
            toDate: moment(schedulerRef.current.instance.endDate),
          },
          currentViewPreset,
          workingDaysConfig,
        );

      const currentVisibleDateRange: Range = {
        fromDate: moment(correctedFromDate),
        toDate: moment(correctedToDate),
      };

      const rangeToBeFetched =
        e.type.toLowerCase() === 'scroll'
          ? currentVisibleDateRange
          : getNewRangeExcludingExistingRanges(
              rangesLoadedInUI,
              currentVisibleDateRange,
            );

      if (rangeToBeFetched == null) {
        // TODO: Need to check if this covers all of the cases
        logger.log('** returning as no new rangeToBeFetched');

        return;
      }

      const resourcesToBeFetched = newResources;
      const rolesToBeFetched = newRoles;

      if (
        Object.keys(resourcesToBeFetched).length === 0 &&
        (['onviewpresetchange', 'change'].includes(e.type.toLowerCase())
          ? Object.keys(rolesToBeFetched).length === 0
          : true)
      ) {
        logger.log(
          '** returning as no new resourcesToBeFetched or rolesToBeFetched',
        );

        return;
      }

      if (showLoaderOnDataFetch) {
        showLoading();
      }

      onVisibleResourceChange?.({
        ...getInitialResourcesToBeFetched(),
        [FetchableResourceType.ProjectList]: getVisibleProjectIds(),
      });
      // Earlier the date ranges were updated when lite call is resolved
      // Moving this out because if there are multiple parallel lite calls, the date ranges overlap
      setRangesLoadedInUI((prev) => [...prev, rangeToBeFetched]);
      setAllLoadedRanges((prev) => [...prev, rangeToBeFetched]);

      loadAllocations?.({
        resources: {
          userIdList: stringListToNumberList(
            Object.keys(
              groupBy === GroupBy.Users
                ? resourcesToBeFetched
                : groupBy === GroupBy.Projects && isCollaborator
                ? { [currentUserId]: true }
                : {},
            ),
          ),
          roleIdList: stringListToNumberList(
            Object.keys(
              e.type.toLowerCase() === 'scroll' ? {} : rolesToBeFetched,
            ),
          ),
          projectIdList: stringListToNumberList(
            Object.keys(
              groupBy === GroupBy.Projects ? resourcesToBeFetched : {},
            ),
          ),
        },
        range: rangeToBeFetched,
      })
        .then((allocations) => {
          if (schedulerRef.current) {
            if (Object.keys(newResources).length > 0) {
              setResourcesLoadedInUI((prev) => ({ ...prev, ...newResources }));
            }
            if (Object.keys(rolesToBeFetched).length > 0) {
              setRolesLoadedInUI((prev) => ({ ...prev, ...rolesToBeFetched }));
            }
            onAllocationLoadHandler(
              allocations,
              Object.keys(resourcesToBeFetched).map((id) => parseInt(id, 10)),
              !isSchedulerLoadedWithData,
            ).then(() => {
              if (!isSchedulerLoadedWithData) {
                setSchedulerLoadedWithData(true);
              }
              if (showLoaderOnDataFetch) {
                hideLoading();
              }
            });
          }
        })
        .catch(() => {
          setRangesLoadedInUI((prev) =>
            prev.filter(
              ({ fromDate, toDate }) =>
                !(
                  fromDate === rangeToBeFetched.fromDate &&
                  toDate === rangeToBeFetched.toDate
                ),
            ),
          );
          setAllLoadedRanges((prev) =>
            prev.filter(
              ({ fromDate, toDate }) =>
                !(
                  fromDate === rangeToBeFetched.fromDate &&
                  toDate === rangeToBeFetched.toDate
                ),
            ),
          );
        });
    },
    [
      currentUserId,
      currentViewPreset,
      getRoleIdsToBeFetched,
      getVisibleProjectIds,
      getVisibleResources,
      groupBy,
      hideLoading,
      isCollaborator,
      isSchedulerLoadedWithData,
      loadAllocations,
      onAllocationLoadHandler,
      onVisibleResourceChange,
      rangesLoadedInUI,
      resourcesLoadedInUI,
      rolesLoadedInUI,
      schedulerRef,
      showLoaderOnDataFetch,
      showLoading,
      workingDaysConfig,
    ],
  );
  const loadAllocationRef = useRef(loadAllocationsForRange);

  useEffect(() => {
    loadAllocationRef.current = loadAllocationsForRange;
  }, [loadAllocationsForRange]);
  const debouncedLoadAllocations = useMemo(
    () =>
      debounce(
        (e) => {
          switch (e.type.toLowerCase()) {
            case 'loaddaterange':
              // Resetting the following state to fix issue of not fetching for all resources;
              setCurrentVisibleResources(getInitialVisibleResourcesRange());
              setResourcesLoadedInUI({});
              setRolesLoadedInUI({});
              break;
            case 'change':
            case 'availabilityfilterchange':
              // Resetting the following state to fix issue of not fetching for all resources;
              setCurrentVisibleResources(getInitialVisibleResourcesRange());
              setResourcesLoadedInUI({});
              setRolesLoadedInUI({});
              setRangesLoadedInUI([]);
              break;
            case 'onviewpresetchange':
              setCurrentVisibleResources(getInitialVisibleResourcesRange());
              setResourcesLoadedInUI({});
              setRolesLoadedInUI({});
              setRangesLoadedInUI([]);
              break;
            case 'scroll':
              setRangesLoadedInUI([]);
              break;
            default:
              // Resetting the following state to fix issue of not fetching for all resources;
              setCurrentVisibleResources({ firstIndex: 0, lastIndex: 0 });
              setResourcesLoadedInUI({});
              setRolesLoadedInUI({});
              setRangesLoadedInUI([]);
              break;
          }
          queueMacrotask(() => {
            loadAllocationRef.current(e);
          });
        },
        500,
        { maxWait: 500 },
      ),
    [setCurrentVisibleResources],
  );

  useEventListenerForEventStore(schedulerRef, 'loadDateRange', (e) => {
    debouncedLoadAllocations(e);
  });

  useEventListenerForScheduler(schedulerRef, 'scroll', (e) => {
    debouncedLoadAllocations(e);
  });

  useEventListenerForScheduler(
    schedulerRef,
    'availabilityFilterChange',
    (e) => {
      debouncedLoadAllocations(e);
    },
  );

  useEventListenerForResourceStore(schedulerRef, 'filter', (e) => {
    debouncedLoadAllocations(e);
  });

  useEventListenerForScheduler(schedulerRef, 'onViewPresetChange', (e) => {
    debouncedLoadAllocations(e);
  });

  return {
    isSchedulerLoadedWithData,
    rangesLoadedInUI: allLoadedRanges,
    resourcesLoadedInUI,
  };
};
